import React, { useState } from 'react';
import './DailyBars.css';

export interface DailyPoint {
  date: string;
  value: number;
}

interface DailyBarsProps {
  title: string;
  points: DailyPoint[];
}

const dayLabel = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('bg-BG', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * One series of daily counts as thin bars, oldest left. Hover or focus a bar to
 * read its day; the table under "Таблица" lists every value.
 */
const DailyBars: React.FC<DailyBarsProps> = ({ title, points }) => {
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(1, ...points.map((p) => p.value));
  const peak = points.reduce((best, p, i) => (p.value > points[best].value ? i : best), 0);

  return (
    <figure className="db-chart">
      <figcaption className="db-head">
        <span className="db-title">{title}</span>
        <span className="db-readout" aria-live="polite">
          {active !== null
            ? `${dayLabel(points[active].date)}: ${points[active].value}`
            : points.length > 0 && `Най-много: ${points[peak].value} (${dayLabel(points[peak].date)})`}
        </span>
      </figcaption>
      <div className="db-plot" onMouseLeave={() => setActive(null)}>
        {points.map((p, i) => (
          <div
            key={p.date}
            className={`db-slot${active === i ? ' is-active' : ''}`}
            tabIndex={0}
            aria-label={`${dayLabel(p.date)}: ${p.value}`}
            onMouseEnter={() => setActive(i)}
            onFocus={() => setActive(i)}
            onBlur={() => setActive(null)}
          >
            <div className="db-bar" style={{ height: p.value === 0 ? 0 : `${Math.max(4, (p.value / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      {points.length > 0 && (
        <div className="db-axis" aria-hidden="true">
          <span>{dayLabel(points[0].date)}</span>
          <span>{dayLabel(points[points.length - 1].date)}</span>
        </div>
      )}
      <details className="db-table">
        <summary>Таблица</summary>
        <table>
          <tbody>
            {[...points].reverse().map((p) => (
              <tr key={p.date}>
                <td>{dayLabel(p.date)}</td>
                <td>{p.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
};

export default DailyBars;
